import { create } from 'zustand'
import { persist } from 'zustand/middleware'
// Define types for our store
type CartItem = {
  id: string
  name: string
  price: number
  quantity: number
}
type User = {
  id: string
  name: string
  email: string
}
type AppState = {
  darkMode: boolean
  cartItems: CartItem[]
  isLoggedIn: boolean
  savedEvents: string[]
  savedJobs: string[]
  user: User | null
  toggleDarkMode: () => void
  setDarkMode: (value: boolean) => void
  addToCart: (item: CartItem) => void
  removeFromCart: (id: string) => void
  updateCartItemQuantity: (id: string, quantity: number) => void
  clearCart: () => void
  login: (user: User) => void
  logout: () => void
  saveEvent: (eventId: string) => void
  unsaveEvent: (eventId: string) => void
  saveJob: (jobId: string) => void
  unsaveJob: (jobId: string) => void
}
// Create store with persistence
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      darkMode: false,
      cartItems: [],
      isLoggedIn: false,
      savedEvents: [],
      savedJobs: [],
      user: null,
      // Dark mode actions
      toggleDarkMode: () => set((state) => ({ darkMode: !state.darkMode })),
      setDarkMode: (value) => set({ darkMode: value }),
      // Cart actions
      addToCart: (item) =>
        set((state) => {
          const existingItem = state.cartItems.find((i) => i.id === item.id)
          if (existingItem) {
            return {
              cartItems: state.cartItems.map((i) =>
                i.id === item.id
                  ? { ...i, quantity: i.quantity + item.quantity }
                  : i,
              ),
            }
          } else {
            return { cartItems: [...state.cartItems, item] }
          }
        }),
      removeFromCart: (id) =>
        set((state) => ({
          cartItems: state.cartItems.filter((item) => item.id !== id),
        })),
      updateCartItemQuantity: (id, quantity) =>
        set((state) => ({
          cartItems: state.cartItems.map((item) =>
            item.id === id ? { ...item, quantity } : item,
          ),
        })),
      clearCart: () => set({ cartItems: [] }),
      // Auth actions
      login: (user) => set({ isLoggedIn: true, user }),
      logout: () => set({ isLoggedIn: false, user: null }),
      // Saved items actions
      saveEvent: (eventId) =>
        set((state) => ({
          savedEvents: [...state.savedEvents, eventId],
        })),
      unsaveEvent: (eventId) =>
        set((state) => ({
          savedEvents: state.savedEvents.filter((id) => id !== eventId),
        })),
      saveJob: (jobId) =>
        set((state) => ({
          savedJobs: [...state.savedJobs, jobId],
        })),
      unsaveJob: (jobId) =>
        set((state) => ({
          savedJobs: state.savedJobs.filter((id) => id !== jobId),
        })),
    }),
    {
      name: 'jointoday-storage', // Name for the persisted storage
    },
  ),
)
